import { z } from 'zod';
import { PlatformRoleSchema } from './platform-role';
import { PopulatedDepartmentSchema } from '../department/department.schema';
import { UserOrgFunctionRefSchema } from '../org-function/org-function-ref.schema';
import { SupportedLanguageSchema } from '../../shared/language.schema';
import { AvatarSchema } from '../../shared/avatar.schema';
import { ErrorCode } from '../../shared/error-codes';
/**
 * The permission types that exist only in customer/demo tenants.
 *
 * Exported so the backend's seat pools (`shared/authorization/seat-limits.ts`)
 * are derived from this enum rather than from a hand-maintained copy: seat
 * accounting gives each of these roles its own capped pool and everyone else
 * the member pool, so a copy that drifted would file a newly added role's
 * holders into the member pool and leave the new role itself uncapped.
 */ export const CustomerUserTypeSchema = z.enum([
    'system_admin',
    'org_manager'
]);
/**
 * The permission types that exist only in partner tenants
 * (`tenantType === 'partner'`).
 *
 * `UserTypeSchema` is composed from this enum instead of re-listing its values,
 * so the partner set can never drift from the full set: a new partner role can
 * only enter the system through here. That matters because `isPartnerUserType`
 * is an authorization predicate (update-org-member decides by it whether a role
 * change is a partner-role change) — a hand-maintained second list would
 * silently reject a valid new role.
 */ export const PartnerUserTypeSchema = z.enum([
    'partner_admin',
    'partner_staff'
]);
/**
 * User Type Enum
 * Defines the permission types a user can have — the customer-tenant and
 * partner-tenant roles combined.
 */ export const UserTypeSchema = z.enum([
    ...CustomerUserTypeSchema.options,
    ...PartnerUserTypeSchema.options
]);
export function isPartnerUserType(value) {
    return PartnerUserTypeSchema.safeParse(value).success;
}
/**
 * The roles a membership may carry, per tenant type. The CASL app split is
 * keyed on `tenantType` and roles only refine within that branch, so a role
 * from the other branch is not a weaker permission — it is none at all
 * (customer roles in a partner tenant) or the wrong surface (partner roles in
 * a customer tenant). Written out per tenant type rather than as "everything
 * except partner", so a new tenant type has to answer the question here.
 */ const USER_TYPES_BY_TENANT_TYPE = {
    partner: PartnerUserTypeSchema.options,
    customer: CustomerUserTypeSchema.options,
    demo: CustomerUserTypeSchema.options
};
/** The roles a tenant of this type may hand out — the UI's option list. */ export function userTypesForTenantType(tenantType) {
    return USER_TYPES_BY_TENANT_TYPE[tenantType];
}
/**
 * The single "may this membership exist?" judgement, shared by every writer of
 * a member's roles or classification (both superadmin tenant-user endpoints and
 * both in-app org-member endpoints). One function so the four writers cannot
 * drift, and so a new writer inherits every rule by calling one thing.
 *
 * Pass `stored` for an update and omit it for a create — that is what tells the
 * two apart. A PATCH payload is partial, so `stored` supplies whatever `update`
 * omits, and roles absent from it are simply not being written; on a create
 * absent roles mean no roles, with no stored value to defer to.
 *
 * The role-branch rule judges only roles actually being written, never the
 * merged state. Judging the merge would make an already-violating member
 * unsaveable — every edit of them, even a rename, refused by the violation the
 * edit is trying to repair.
 */ export function memberInvariantError(tenantType, update, stored) {
    const writtenRoles = stored ? update.userTypes : update.userTypes ?? [];
    if (writtenRoles !== undefined) {
        // Without a tenant type there is no branch to judge against, so refuse:
        // every caller writing roles is tenant-scoped, and a missing type means the
        // request never resolved its tenant.
        if (tenantType === undefined || writtenRoles.some((userType)=>!userTypesForTenantType(tenantType).includes(userType))) {
            return {
                errorCode: ErrorCode.USER_TYPES_TENANT_TYPE_MISMATCH,
                field: 'userTypes'
            };
        }
        // The empty set is a valid customer membership (an ordinary employee holds
        // no elevated role) but never a valid partner one — the partner area has no
        // roleless surface, so such a member logs in to a dashboard that 403s.
        if (tenantType === 'partner' && writtenRoles.length === 0) {
            return {
                errorCode: ErrorCode.PARTNER_MEMBER_REQUIRES_ROLE,
                field: 'userTypes'
            };
        }
    }
    const classification = update.classification ?? stored?.classification;
    // A partner tenant's members are the partner firm's own staff, and the
    // partner CASL branch grants by role alone — an external partner member is
    // additionally unsatisfiable, since the external-member rule below can only
    // be met with a customer role.
    if (tenantType === 'partner' && classification === 'external') {
        return {
            errorCode: ErrorCode.PARTNER_MEMBER_MUST_BE_INTERNAL,
            field: 'classification'
        };
    }
    if (classification === 'external' && !hasExternalMemberRole(writtenRoles ?? stored?.userTypes)) {
        return {
            errorCode: ErrorCode.EXTERNAL_MEMBER_REQUIRES_ROLE,
            field: 'userTypes'
        };
    }
    return undefined;
}
/**
 * User Status Enum
 * Tracks the lifecycle status of a user
 * - inactive: User created but not yet invited
 * - pending: Invitation sent, awaiting activation
 * - active: User activated and can access the system
 * - suspended: Account temporarily blocked (can be reactivated)
 *
 * State transitions: inactive → pending → active ↔ suspended
 */ export const UserStatusSchema = z.enum([
    'inactive',
    'pending',
    'active',
    'suspended'
]);
/**
 * User Classification Enum
 * Defines whether a user is internal or external to the organization
 */ export const UserClassificationSchema = z.enum([
    'internal',
    'external'
]);
/**
 * Provenance marker. 'initial-setup' marks wizard-imported users, the only ones
 * a replace-style re-import (ADR 0011) may delete; 'data-sync' marks users
 * an Abgleich created. Superadmin-precreated users never have it and survive.
 */ export const UserCreatedViaSchema = z.enum([
    'initial-setup',
    'data-sync'
]);
/**
/**
 * The minimum a surface needs to render a person as an avatar chip: no `_id`,
 * no email, nothing that identifies the account behind the face. Composed by
 * every wire shape that shows WHO without exposing the user record.
 */ export const UserDisplayIdentitySchema = z.object({
    name: z.string(),
    initials: z.string(),
    avatarUrl: z.string().optional()
});
/**
 * ManagerUserSchema — populated user reference WITHOUT the `department` field.
 *
 * Defined explicitly (not via `.omit(PopulatedUserSchema, 'department')`) so it
 * has no recursive reference: a Department populates its `manager` as a
 * ManagerUser, but a ManagerUser does NOT have a populated department, which
 * breaks the schema-level cycle both at TypeScript type-inference time AND at
 * runtime Zod parse time (no infinite descent).
 */ export const ManagerUserSchema = z.object({
    _id: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    email: z.string(),
    avatar: AvatarSchema.optional(),
    mainRole: UserOrgFunctionRefSchema.optional(),
    secondaryRoles: z.array(UserOrgFunctionRefSchema).optional(),
    userTypes: z.array(UserTypeSchema),
    status: UserStatusSchema,
    classification: UserClassificationSchema
});
/**
 * User Schema
 * Represents a user entity in the system
 */ export const UserSchema = z.object({
    _id: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    email: z.email(),
    avatar: AvatarSchema.optional(),
    department: z.lazy(()=>PopulatedDepartmentSchema).optional(),
    mainRole: UserOrgFunctionRefSchema.optional(),
    secondaryRoles: z.array(UserOrgFunctionRefSchema).optional(),
    location: z.string().optional(),
    userTypes: z.array(UserTypeSchema),
    /**
   * Was dieser Mensch an der Plattform darf (ADR 0042). Leer bei jedem, der
   * in einem Mandanten lebt — dort entscheiden `userTypes`.
   */ platformRoles: z.array(PlatformRoleSchema).optional(),
    status: UserStatusSchema,
    classification: UserClassificationSchema,
    invitedAt: z.number().optional(),
    activatedAt: z.number().optional(),
    createdVia: UserCreatedViaSchema.optional(),
    externalOrganization: z.string().optional(),
    language: SupportedLanguageSchema.optional(),
    /**
   * HubSpot contact-record id of this user's employee contact (only meaningful
   * for partner-tenant users). Links a Bliks partner user to their HubSpot
   * contact so we can attribute leads ("Akquisiteur"), scope "my leads" for
   * partner_staff, and drive the invite-from-HubSpot flow.
   */ hubspotContactId: z.string().optional(),
    /**
   * Free-text job title / position (only meaningful for partner-tenant users).
   * Captured from the employee's HubSpot contact at invite time, or entered in
   * the manual invite dialog. Shown on the partner team cards.
   */ jobTitle: z.string().optional(),
    deletedAt: z.number().optional()
});
/**
 * Populated User Schema
 * Used when user references are populated with full user data
 * Simplified version for display purposes (e.g., in UserCard component)
 *
 * Note: This schema is used when the backend populates user references in responses.
 * It contains fewer fields than UserSchema but keeps critical fields like userTypes,
 * status, and classification as required to prevent runtime errors.
 *
 * Use cases:
 * - UserSchema: Full user entity with all database fields
 * - PopulatedUserSchema: Populated user references in API responses (departments, teams, processes, etc.)
 */ export const PopulatedUserSchema = ManagerUserSchema.extend({
    department: z.lazy(()=>PopulatedDepartmentSchema).optional(),
    deletedAt: z.number().optional()
});
/**
 * A user is "never invited" while still `inactive` — created but no invitation
 * has ever been sent (see `UserStatusSchema`). This is the exact set the
 * bulk-invite action targets; a single resend to an already-`pending` user is
 * handled by the per-row action instead. Canonical so the frontend filter and
 * the backend gate share one definition of the concept.
 */ export const isNeverInvited = (status)=>status === 'inactive';
/**
 * Whether this user can still reach the app. `pending` counts — an invited
 * user activates on their first sign-in, so they are a legitimate target for
 * work that waits for them. `inactive` (never invited) and `suspended` cannot,
 * so handing either of them a task strands it where nobody will see it.
 */ export const canSignIn = (status)=>status === 'pending' || status === 'active';
/**
 * External members have no department and no Stelle, so every department- and
 * role-scoped surface in the app is empty for them. Without an elevated role
 * they would have no reachable page at all. We therefore require every external
 * member to carry at least one of these roles. Currently that is every UserType,
 * but the check stays explicit so adding a non-elevated UserType later does not
 * silently weaken it. Adding a UserType outside this list also invalidates the
 * EXTERNAL_MEMBER_REQUIRES_ROLE copy, which says "at least one user type".
 *
 * They CAN own a process (the owner moderates and never records) and can be
 * nominated to record on an external contact's behalf — neither depends on a
 * department, so neither is a reason for this rule.
 */ export const EXTERNAL_MEMBER_REQUIRED_ROLES = [
    'org_manager',
    'system_admin'
];
/** Role pre-selected when a member is switched to external — must satisfy the
 * invariant above so the form is valid by construction. */ export const DEFAULT_EXTERNAL_MEMBER_ROLE = EXTERNAL_MEMBER_REQUIRED_ROLES[0];
/**
 * Classification test for a stored user document. Distinct from
 * `hasExternalMemberRole`, which tests roles: a member is external because of
 * where they sit in the org, not because of what they may do.
 */ export function isExternalMember(user) {
    return user.classification === 'external';
}
export function hasExternalMemberRole(userTypes) {
    return !!userTypes?.some((type)=>EXTERNAL_MEMBER_REQUIRED_ROLES.includes(type));
}
/**
 * Shared zod refinement for the two external-member cross-field rules
 * (externalOrganization required, elevated role required). `partial: true`
 * skips the role rule when userTypes is absent from a PATCH payload — the
 * server judges those against the stored document via memberInvariantError.
 * `pathPrefix` targets nested payloads (e.g. add-tenant-user's `user` object).
 */ export function refineExternalMember(ctx, data, opts = {}) {
    if (data.classification !== 'external') return;
    const prefix = opts.pathPrefix ?? [];
    if (!data.externalOrganization) {
        ctx.addIssue({
            code: 'custom',
            message: ErrorCode.FIELD_REQUIRED,
            path: [
                ...prefix,
                'externalOrganization'
            ]
        });
    }
    const roleUnknown = opts.partial && data.userTypes === undefined;
    if (!roleUnknown && !hasExternalMemberRole(data.userTypes)) {
        ctx.addIssue({
            code: 'custom',
            message: ErrorCode.EXTERNAL_MEMBER_REQUIRES_ROLE,
            path: [
                ...prefix,
                'userTypes'
            ]
        });
    }
}

//# sourceMappingURL=user.schema.js.map